import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Divider } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HistoryIcon from '@mui/icons-material/History';
import { useNavigate } from 'react-router-dom';
import { DashboardService } from '../service';

export default function ActivityFeed({ initialData }) {
    const navigate = useNavigate();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);

    const formatLogTime = (ts) => {
        try {
            const d = new Date(ts);
            if (isNaN(d.getTime())) return String(ts);
            return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
        } catch {
            return 'Just now';
        }
    };

    const parseActivityItem = (item, idx) => {
        const actorName = item.performedBy?.name || item.user?.name || item.userName || item.actor || item.user || 'Staff';
        
        let desc = item.description;
        if (!desc) {
            const action = item.action || 'Updated';
            const entity = item.entityName || item.module || item.target || '';
            const shortId = item.entityId ? (item.entityId.length > 8 ? `#${item.entityId.slice(0, 8)}` : `#${item.entityId}`) : '';
            if (entity) {
                desc = `${action} ${entity}${shortId ? ` • ${shortId}` : ''}`;
            } else {
                desc = item.message || `${action} system status`;
            }
        }

        return {
            id: item.id || `activity-${idx}`,
            avatar: actorName.charAt(0).toUpperCase(),
            actor: actorName,
            time: item.time || (item.timestamp ? formatLogTime(item.timestamp) : (item.createdAt ? formatLogTime(item.createdAt) : 'Recent')),
            description: desc
        };
    };

    const fetchActivity = async () => {
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';
            const res = await DashboardService.fetchActivityLog(tenantId);
            const dataArray = Array.isArray(res) ? res : (res && Array.isArray(res.data) ? res.data : (res && Array.isArray(res.activities) ? res.activities : null));
            if (dataArray && dataArray.length > 0) {
                const mapped = dataArray.slice(0, 4).map(parseActivityItem);
                setActivities(mapped);
            } else {
                setActivities([]);
            }
        } catch (err) {
            console.error('Failed to fetch activity logs:', err);
            setActivities([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialData && Array.isArray(initialData) && initialData.length > 0) {
            const mapped = initialData.slice(0, 4).map(parseActivityItem);
            setActivities(mapped);
        } else if (initialData && Array.isArray(initialData) && initialData.length === 0) {
            setActivities([]);
        } else {
            fetchActivity();
        }
    }, [initialData]);

    const items = activities;

    return (
        <Card sx={{ 
            borderRadius: 3, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)', 
            border: '1px solid #f1f5f9', 
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
        }}>
            <CardContent sx={{ p: 2.5, pb: 1.5, flexGrow: 1 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.2 }}>
                            Activity
                        </Typography>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981' }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.78rem', fontWeight: 500 }}>
                        Live
                    </Typography>
                </Box>

                {/* Activity List or Empty State */}
                {items.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {items.map((item, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                {/* Avatar Letter Box */}
                                <Box sx={{ 
                                    width: 28, 
                                    height: 28, 
                                    borderRadius: '6px', 
                                    bgcolor: '#f1f5f9', 
                                    color: '#475569', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    flexShrink: 0,
                                    mt: 0.25
                                }}>
                                    {item.avatar}
                                </Box>

                                {/* Details */}
                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
                                            {item.actor}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                                            {item.time}
                                        </Typography>
                                    </Box>
                                    <Typography 
                                        variant="caption" 
                                        noWrap 
                                        sx={{ 
                                            color: '#64748b', 
                                            fontSize: '0.75rem', 
                                            display: 'block', 
                                            lineHeight: 1.4,
                                            maxWidth: '100%' 
                                        }}
                                    >
                                        {item.description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                ) : (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        py: 3.5, 
                        textAlign: 'center' 
                    }}>
                        <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '50%',
                            bgcolor: '#f1f5f9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1.5
                        }}>
                            <HistoryIcon sx={{ fontSize: 24, color: '#64748b' }} />
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.85rem' }}>
                            No Recent Activity
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.5, maxWidth: 220 }}>
                            Operational events and staff logs will be displayed here in real time.
                        </Typography>
                    </Box>
                )}
            </CardContent>

            {/* Footer Link */}
            <Box>
                <Divider sx={{ borderColor: '#f1f5f9' }} />
                <Box 
                    onClick={() => navigate('/security')}
                    sx={{ 
                        p: 1.5, 
                        px: 2.5,
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: '#64748b',
                        transition: 'all 0.2s',
                        '&:hover': { color: 'primary.main', bgcolor: 'rgba(99, 102, 241, 0.02)' }
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        Activity Log
                    </Typography>
                    <ChevronRightIcon sx={{ fontSize: 16 }} />
                </Box>
            </Box>
        </Card>
    );
}
